import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import PrimaryTabNavigation from 'components/ui/PrimaryTabNavigation';
import { formatDurationLabel } from 'utils/videoUtils';

const STORAGE_KEY = 'gameforge_games';

function loadCreations() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return [];
}

const TYPE_LABELS = {
  'text-to-game': 'Game',
  'prompt-to-mini-game': 'Mini Game',
  'text-to-animation': 'Animation',
  'script-to-video': 'Video',
  'music-to-video': 'Music Video',
  'text-to-music': 'Music',
  'lip-sync': 'Lip Sync',
  'image-to-video': 'Image Video',
};

const TYPE_ICONS = {
  'text-to-game': 'Gamepad2',
  'prompt-to-mini-game': 'Zap',
  'text-to-animation': 'Film',
  'script-to-video': 'Video',
  'music-to-video': 'Music',
  'text-to-music': 'Music2',
  'lip-sync': 'Mic',
  'image-to-video': 'ImagePlay',
};

const TYPE_COLORS = {
  'text-to-game': 'var(--color-primary)',
  'prompt-to-mini-game': 'var(--color-secondary)',
  'text-to-animation': 'var(--color-accent)',
  'script-to-video': '#10B981',
  'music-to-video': '#F43F5E',
  'text-to-music': '#8B5CF6',
  'lip-sync': '#EC4899',
  'image-to-video': '#06B6D4',
};

const FILTER_TABS = [
  { id: 'all', label: 'All', icon: 'Layers' },
  { id: 'music-to-video', label: 'Music Videos', icon: 'Music' },
  { id: 'text-to-game', label: 'Games', icon: 'Gamepad2' },
  { id: 'script-to-video', label: 'Videos', icon: 'Video' },
  { id: 'text-to-animation', label: 'Animations', icon: 'Film' },
];

export default function LibraryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [creations] = useState(() => loadCreations());
  const [activeFilter, setActiveFilter] = useState(location?.state?.filter || 'all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (location?.state?.filter) {
      setActiveFilter(location?.state?.filter);
    }
  }, [location?.state?.filter]);

  const filtered = creations?.filter((c) => {
    const matchesFilter = activeFilter === 'all' || c?.mode === activeFilter;
    const matchesSearch = !searchQuery || c?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handlePlay = (item) => {
    const mode = item?.mode || '';
    if (mode === 'text-to-game' || mode === 'prompt-to-mini-game') {
      navigate('/game-player-screen', { state: { game: item } });
    } else if (mode === 'script-to-video') {
      navigate(`/video/${item?.id}`, { state: { item } });
    } else if (mode === 'music-to-video') {
      // Always pass the full item so preview gets correct videoId and videoUrl
      navigate(`/music-video/${item?.id}`, { state: { item: { ...item } } });
    } else if (mode === 'text-to-animation') {
      navigate(`/animation/${item?.id}`, { state: { item } });
    } else {
      navigate('/game-player-screen', { state: { game: item } });
    }
  };

  const handleDownload = (item) => {
    const url = item?.videoUrl;
    if (!url || url === '#') {
      // No video URL available — show feedback
      alert('Video file is no longer available. Please re-render this video in the Creator Studio.');
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    const ext = item?.mimeType?.includes('mp4') ? 'mp4' : 'webm';
    link.download = `${item?.title || 'creation'}-${item?.videoId || item?.id || 'export'}.${ext}`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  const handleRemix = (item) => {
    navigate('/creator-dashboard', { state: { remixItem: item } });
  };

  const handleEdit = (item) => {
    navigate('/creator-dashboard', { state: { remixItem: item, mode: item?.mode } });
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PrimaryTabNavigation />
      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>My Library</h1>
              <p className="text-sm mt-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                {creations?.length} creation{creations?.length !== 1 ? 's' : ''} · Music Videos, Games, Animations &amp; More
              </p>
            </div>
            <button
              onClick={() => navigate('/creator-dashboard')}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
              style={{ background: 'var(--color-primary)', color: 'white' }}
            >
              <Icon name="Plus" size={16} color="white" />
              Create New
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search your creations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm"
              style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)', color: 'var(--color-foreground)', fontFamily: 'var(--font-body)' }}
            />
          </div>

          {/* Filter tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
            {FILTER_TABS?.map((tab) => {
              const count = tab?.id === 'all' ? creations?.length : creations?.filter((c) => c?.mode === tab?.id)?.length;
              const active = activeFilter === tab?.id;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveFilter(tab?.id)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all"
                  style={{
                    background: active ? 'var(--color-primary)' : 'var(--color-card)',
                    color: active ? 'white' : 'var(--color-muted-foreground)',
                    border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  }}
                >
                  <Icon name={tab?.icon} size={13} color={active ? 'white' : 'var(--color-muted-foreground)'} />
                  {tab?.label}
                  <span className="px-1.5 py-0.5 rounded-full text-xs" style={{ background: active ? 'rgba(255,255,255,0.25)' : 'var(--color-muted)', color: active ? 'white' : 'var(--color-muted-foreground)' }}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Grid */}
          {filtered?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'var(--color-muted)' }}>
                <Icon name="Library" size={32} color="var(--color-muted-foreground)" />
              </div>
              <div className="text-center">
                <p className="text-lg font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>No creations yet</p>
                <p className="text-sm mt-1" style={{ color: 'var(--color-muted-foreground)' }}>Start creating in the Creator Studio</p>
              </div>
              <button
                onClick={() => navigate('/creator-dashboard')}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold"
                style={{ background: 'var(--color-primary)', color: 'white' }}
              >
                <Icon name="Wand2" size={15} color="white" />
                Open Creator Studio
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered?.map((item) => {
                const typeColor = TYPE_COLORS?.[item?.mode] || 'var(--color-primary)';
                const typeLabel = TYPE_LABELS?.[item?.mode] || 'Creation';
                const typeIcon = TYPE_ICONS?.[item?.mode] || 'Wand2';
                const isMusicVideo = item?.mode === 'music-to-video';
                const dur = formatDurationLabel(item?.duration);
                return (
                  <div
                    key={item?.id}
                    className="rounded-2xl overflow-hidden flex flex-col transition-all hover:scale-[1.02] hover:shadow-lg"
                    style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)' }}
                  >
                    {/* Thumbnail */}
                    <div className="relative" style={{ aspectRatio: '16/9', background: '#0a0a0f' }}>
                      {item?.thumbnail ? (
                        <Image src={item?.thumbnail} alt={item?.thumbnailAlt || item?.title || 'Creation thumbnail'} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Icon name={typeIcon} size={32} color={typeColor} />
                        </div>
                      )}
                      {/* Type badge */}
                      <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold" style={{ background: `${typeColor}dd`, color: 'white', backdropFilter: 'blur(4px)' }}>
                        <Icon name={typeIcon} size={11} color="white" />
                        {typeLabel}
                      </div>
                      {/* Duration badge */}
                      {dur && (
                        <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-lg text-xs" style={{ background: 'rgba(0,0,0,0.7)', color: 'white' }}>
                          <Icon name="Clock" size={10} color="white" />
                          {dur}
                        </div>
                      )}
                      {/* Play overlay */}
                      <button
                        onClick={() => handlePlay(item)}
                        className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                        style={{ background: 'rgba(0,0,0,0.5)' }}
                        aria-label={`Play ${item?.title}`}
                      >
                        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: typeColor, boxShadow: `0 0 20px ${typeColor}80` }}>
                          <Icon name="Play" size={20} color="white" />
                        </div>
                      </button>
                    </div>

                    {/* Info */}
                    <div className="p-3 flex flex-col gap-2 flex-1">
                      <div>
                        <h3 className="text-sm font-bold truncate" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{item?.title}</h3>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{item?.createdAt || 'Recently created'}</p>
                      </div>

                      {/* Music video metadata */}
                      {isMusicVideo && (
                        <div className="flex flex-wrap gap-1">
                          {item?.bpm && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(244,63,94,0.1)', color: '#F43F5E', fontFamily: 'var(--font-caption)' }}>
                              🎵 {item?.bpm} BPM
                            </span>
                          )}
                          {item?.visualStyle && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(139,92,246,0.1)', color: '#8B5CF6', fontFamily: 'var(--font-caption)' }}>
                              {item?.visualStyle}
                            </span>
                          )}
                          {item?.captionMode && item?.captionMode !== 'none' && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981', fontFamily: 'var(--font-caption)' }}>
                              CC
                            </span>
                          )}
                          {item?.videoUrl && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981', fontFamily: 'var(--font-caption)' }}>
                              ▶ Video
                            </span>
                          )}
                          {item?.durationMode && item?.durationMode !== 'full' && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(6,182,212,0.1)', color: '#06B6D4', fontFamily: 'var(--font-caption)' }}>
                              {item?.durationMode}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className="flex gap-2 mt-auto">
                        <button
                          onClick={() => handlePlay(item)}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
                          style={{ background: typeColor, color: 'white' }}
                        >
                          <Icon name="Play" size={12} color="white" />
                          {isMusicVideo ? 'Watch' : 'Play'}
                        </button>
                        {isMusicVideo && (
                          <button
                            onClick={() => handleEdit(item)}
                            className="flex items-center justify-center w-8 h-8 rounded-xl border transition-all hover:opacity-80"
                            style={{ borderColor: 'rgba(139,92,246,0.4)', background: 'rgba(139,92,246,0.08)' }}
                            aria-label="Edit"
                          >
                            <Icon name="Pencil" size={14} color="#8B5CF6" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDownload(item)}
                          className="flex items-center justify-center w-8 h-8 rounded-xl border transition-all hover:opacity-80"
                          style={{ borderColor: 'var(--color-border)', background: 'var(--color-muted)' }}
                          aria-label="Download"
                        >
                          <Icon name="Download" size={14} color="var(--color-muted-foreground)" />
                        </button>
                        <button
                          onClick={() => handleRemix(item)}
                          className="flex items-center justify-center w-8 h-8 rounded-xl border transition-all hover:opacity-80"
                          style={{ borderColor: 'var(--color-border)', background: 'var(--color-muted)' }}
                          aria-label="Remix"
                        >
                          <Icon name="Shuffle" size={14} color="var(--color-muted-foreground)" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
